import { gql } from '@apollo/client';
import { CbDeliverableV2Fields, DueDateFields } from './fragments';
import { CampaignOfferCounterProposalFields } from './counterOffer/fragments';

export const GET_MY_CAMPAIGN_OFFERS = gql`
  query GetMyCampaignOffers($input: GetMyCampaignOffersInput!) {
    getMyCampaignOffers(input: $input) {
      entities {
        id
        status
        statusV2
        attention
        counterOfferAttention
        appliedAt
        appliedAt
        submittedAt
        declinedAt
        estimatedInfluencerFee
        estimatedTotalFee
        estimatedProcessingFee
        recipientAddress {
          firstName
          lastName
          addressLine1
          addressLine2
          city
          state
          stateCode
          country
          phone
          zipCode
        }
        deliverables {
          id
          agencyFeedback
          postCopy
          status
          createdAt
          files {
            id
            key
            url
            name
          }
          items {
            id
            type
            ... on CBDeliverableFileItem {
              id
              key
              name
              task {
                id
                type
                title
                description
                orderIdx
                hrType
                hrPlatform
                ... on CampaignContentTask {
                  mediaType
                }
              }
              type
              url
            }
            ... on CBDeliverableCopy {
              copy
            }
          }
        }
        deliverablesV2 {
          ...CbDeliverableV2Fields
        }
        submissions {
          id
          type
          ... on CampaignIGLiveSubmission {
            imageKey
            url
          }
          ... on CampaignTTShareSubmission {
            ttShareLink: link
            ttShareImageKey: imageKey
            ttUrl: url
          }
          ... on CampaignIGShareSubmission {
            igShareLink: link
            igShareImageKey: imageKey
            igShareUrl: url
          }
          ... on CampaignTWShareSubmission {
            twShareLink: link
            twShareImageKey: imageKey
            twUrl: url
          }
          ... on CampaignIGPostSubmission {
            link
          }
          ... on CampaignIGReelSubmission {
            link
          }
          ... on CampaignIGStorySubmission {
            imageKey
            url
          }
          ... on CampaignTTVideoSubmission {
            link
          }
          ... on CampaignTTLiveSubmission {
            imageKey
            url
          }
          ... on CampaignTTStorySubmission {
            imageKey
            url
          }
          ... on CampaignTWTweetSubmission {
            link
          }
          ... on CampaignTWLiveSubmission {
            link
          }
          ... on CampaignTWVideoSubmission {
            link
          }
          ... on CampaignLiveAppearanceSubmission {
            streamCohostToken
            event {
              schedule {
                timezone
              }
            }
            stream {
              id
              streamStatus
              name
              description
              slug
              imageFileKey
              imageURL
              mainImageUrl
              scheduleDateTz
              scheduleDateUtc
              isPurchased
              hashtags {
                id
                name
              }
              mentions {
                id
                mentionType
                store {
                  id
                }
                targetType
                unregisteredContact {
                  id
                }
              }
              store {
                slug
              }
            }
          }
          ... on CampaignRecordedAppearanceSubmission {
            streamCohostToken
            event {
              schedule {
                timezone
              }
            }
            draftStream {
              id
              description
              slug
              imageFileKey
              imageURL
              mainImageUrl
              name
              streamStatus
              scheduleDateTz
              scheduleDateUtc
              isPurchased
              hashtags {
                id
                name
              }
              mentions {
                id
                mentionType
                store {
                  id
                }
                targetType
                unregisteredContact {
                  id
                }
              }
              store {
                slug
              }
            }
          }
        }
        campaign {
          budget
          id
          name
          status
          type
          createdAt
          dueDate
          applicationDeadlineEnabled
          usageRights
          usageRightsText
          search {
            sports
            smPlatforms
          }
          activities {
            id
            type
            ... on SocialActivity {
              id
              type
              brandName
              productValue
            }
            ... on SocialShareActivity {
              id
              type
              brandName
            }
            ... on UgcActivity {
              id
              type
              brandName
              productValue
            }
          }
          agency {
            id
            agencyDetails {
              id
              companyName
            }
          }
        }
        isPaymentFailed
        counterOffers {
          ...CampaignOfferCounterProposalFields
        }
        originalInfluencerFee
        originalTotalFee
        originalProcessingFee
      }
      limit
      offset
      total
    }
  }
  ${CbDeliverableV2Fields},
  ${CampaignOfferCounterProposalFields}
`;

export const GET_MY_CAMPAIGN_OFFER_BY_CAMPAIGN_ID = gql`
  query GetMyCampaignOfferByCampaignId(
    $input: GetMyCampaignOfferByCampaignInput!
  ) {
    getMyCampaignOfferByCampaignId(input: $input) {
      id
      appliedAt
      appliedAt
      submittedAt
      declinedAt
      approvedAt
      statusV2
      attention
      counterOfferAttention
      canComplete
      hasSignedAgreement
      estimatedInfluencerFee
      estimatedTotalFee
      estimatedProcessingFee
      recipientAddress {
        firstName
        lastName
        addressLine1
        addressLine2
        city
        state
        stateCode
        country
        phone
        zipCode
      }
      deliverables {
        id
        agencyFeedback
        postCopy
        status
        createdAt
        files {
          id
          key
          url
          name
        }
        items {
          id
          type
          task {
            id
            type
            title
            description
            orderIdx
            hrType
            hrPlatform
            ... on CampaignContentTask {
              mediaType
            }
          }
          ... on CBDeliverableFileItem {
            key
            name
            url
          }
          ... on CBDeliverableCopy {
            copy
          }
        }
      }
      deliverablesV2 {
        ...CbDeliverableV2Fields
      }
      submissions {
        id
        type
        task {
          id
        }
        createdAt
        ... on CampaignLiveAppearanceSubmission {
          streamCohostToken
          event {
            startTs
            schedule {
              timezone
            }
          }
          stream {
            id
            streamStatus
            name
            description
            slug
            imageFileKey
            imageURL
            mainImageUrl
            scheduleDateTz
            scheduleDateUtc
            isPurchased
            hashtags {
              id
              name
            }
            mentions {
              id
              mentionType
              store {
                id
              }
              targetType
              unregisteredContact {
                id
              }
            }
            store {
              slug
            }
          }
        }
        ... on CampaignRecordedAppearanceSubmission {
          streamCohostToken
          event {
            startTs
            schedule {
              timezone
            }
          }
          draftStream {
            id
            description
            slug
            imageFileKey
            imageURL
            mainImageUrl
            name
            streamStatus
            scheduleDateTz
            scheduleDateUtc
            isPurchased
            hashtags {
              id
              name
            }
            mentions {
              id
              mentionType
              store {
                id
              }
              targetType
              unregisteredContact {
                id
              }
            }
            store {
              slug
            }
          }
        }
        ... on CampaignIGLiveSubmission {
          imageKey
          url
        }
        ... on CampaignTTShareSubmission {
          ttShareLink: link
          ttShareImageKey: imageKey
          ttUrl: url
        }
        ... on CampaignIGShareSubmission {
          igShareLink: link
          igShareImageKey: imageKey
          igShareUrl: url
        }
        ... on CampaignTWShareSubmission {
          twShareLink: link
          twShareImageKey: imageKey
          twUrl: url
        }
        ... on CampaignIGPostSubmission {
          link
        }
        ... on CampaignIGReelSubmission {
          link
        }
        ... on CampaignIGStorySubmission {
          imageKey
          url
        }
        ... on CampaignTTVideoSubmission {
          link
        }
        ... on CampaignTTLiveSubmission {
          imageKey
          url
        }
        ... on CampaignTTStorySubmission {
          imageKey
          url
        }
        ... on CampaignTWLiveSubmission {
          link
        }
        ... on CampaignTWTweetSubmission {
          link
        }
        ... on CampaignTWVideoSubmission {
          link
        }
      }
      campaign {
        id
        v
        name
        dueDate
        applicationDeadlineEnabled
        requiresAgreement
        status
        type
        createdAt
        usageRights
        usageRightsText
        usageRightsDuration {
          years
          months
        }
        usageRightsScopes
        agency {
          avatarUrl
          storeDetails {
            avatarURL
          }
        }
        search {
          sports
          smPlatforms
        }
        questions {
          id
          isRequired
          order
          question
          type
        }
        activities {
          ... on SocialActivity {
            id
            type
            brandName
            website
            instructions
            encouraged
            avoid
            suggestedCaption
            hashtags
            profileMentions
            promoUrls
            promoCodes
            quantityInstructions
            script
            physicalProduct
            physicalProductType
            productName
            productValue
            submissionDue
            postDueTo
            postDueFrom
            mediaType
            tasks {
              id
              type
              orderIdx
              ... on CampaignContentTask {
                mediaType
              }
              title
              description
            }
            tasksV2 {
              id
              type
              hrType
              hrPlatform
              orderIdx
              title
              description
              suggestedCaption
              ... on CampaignContentTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGLiveTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGPostTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGReelTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGStoryTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTVideoTask {
                dueDate {
                  ...DueDateFields
                }
              }
              contentItems {
                id
                type
                title
                description
                orderIdx
                ... on CampaignContentTask {
                  mediaType
                  dueDate {
                    ...DueDateFields
                  }
                }
              }
            }
            files {
              id
              key
              url
            }
          }
          ... on SocialShareActivity {
            id
            type
            brandName
            instructions
            shareUrl
            type
            ssWebsite: website
            ssDescription: description
            tasks {
              id
              type
            }
          }
          ... on SocialBoostActivity {
            id
            type
            brandName
            caption
            postUrl
            tags
            website
            linkStickerTitle
            linkStickerUrl
            tasksV2 {
              id
              type
            }
          }
          ... on CustomActivity {
            type
            instructions
            files {
              url
              key
            }
          }
          ... on CustomBriefActivity {
            id
            type
            instructions
            brandName
            website
            encouraged
            avoid
            physicalProduct
            productValue
            promoCodes
            promoUrls
            products {
              name
              description
              cost
              qty
              shipping
            }
            tasksV2 {
              id
              type
              hrType
              hrPlatform
              orderIdx
              title
              description
              suggestedCaption
              examples {
                id
                key
                url
              }
              hashtags
              mentions
              tagAsCollab
              ... on CampaignIGLiveTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGPostTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGReelTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGStoryTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTVideoTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTLiveTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTStoryTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignContentTask {
                mediaType
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignAppearanceTask {
                appearanceType
                durationText
                location
                travelIncluded
                accommodationIncluded
                maxExpenses
                dueDate {
                  ...DueDateFields
                }
              }
              contentItems {
                id
                type
                title
                description
                orderIdx
                ... on CampaignContentTask {
                  mediaType
                  dueDate {
                    ...DueDateFields
                  }
                }
              }
            }
            contracts {
              key
              url
            }
          }
          ... on VirtualAppearanceActivity {
            type
            description
            duration
            files {
              url
              key
            }
            instructions
            location
            possibleDates
          }
          ... on InPersonAppearanceActivity {
            id
            address
            city
            country
            coveredExpenses
            description
            duration
            files {
              key
              url
            }
            instructions
            possibleDates
            state
            venueName
            zip
          }
          ... on SponsoredAppearanceActivity {
            id
            type
            videoType
            withAmplify
            tasks {
              id
              type
            }
            target {
              ... on SponsoredAppearanceGuest {
                id
                type
                guestBio: bio
                company
                email
                instagram
                instructions
                linkedin
                name
                otherSocial
              }
              ... on SponsoredAppearanceProduct {
                id
                type
                productBio: bio
                instagram
                instructions
                linkedin
                name
                otherSocial
                physicalProduct
                physicalProductType
                productName
                productValue
                website
                files {
                  url
                  key
                }
              }
            }
          }
          ... on UgcActivity {
            id
            type
            brandName
            website
            instructions
            encouraged
            avoid
            promoUrls
            promoCodes
            script
            physicalProduct
            physicalProductType
            productName
            productValue
            contentDueDate
            tasks {
              id
              type
              orderIdx
              title
              description
              ... on CampaignContentTask {
                mediaType
                dueDate {
                  ...DueDateFields
                }
              }
            }
            files {
              id
              key
              url
            }
          }
        }
        agency {
          id
          avatarUrl
        }
      }
      isPaymentFailed
      counterOffers {
        ...CampaignOfferCounterProposalFields
      }
      originalInfluencerFee
      originalTotalFee
      originalProcessingFee
    }
  }
  ${CbDeliverableV2Fields},
  ${DueDateFields},
  ${CampaignOfferCounterProposalFields}
`;

export const GET_DELIVERABLE_REVISION_LIMIT = gql`
  query GetDeliverableRevisionLimit {
    getDeliverableRevisionLimit
  }
`;

export const IS_INTERVIEW_SCHEDULE_SET = gql`
  query IsInterviewScheduleSet {
    isInterviewScheduleSet
  }
`;

export const GET_MY_CAMPAIGN_OFFER_REDUCED = gql`
  query GetMyCampaignOfferReduced($input: GetMyCampaignOfferInput!) {
    getMyCampaignOffer(input: $input) {
      id
      campaign {
        id
      }
    }
  }
`;

export const GET_MY_CAMPAIGN_OFFER = gql`
  query GetMyCampaignOffer($input: GetMyCampaignOfferInput!) {
    getMyCampaignOffer(input: $input) {
      id
      status
      statusV2
      appliedAt
      appliedAt
      submittedAt
      declinedAt
      estimatedInfluencerFee
      estimatedTotalFee
      recipientAddress {
        firstName
        lastName
        addressLine1
        addressLine2
        city
        state
        stateCode
        country
        phone
        zipCode
      }
      deliverables {
        id
        agencyFeedback
        postCopy
        status
        createdAt
        files {
          id
          key
          url
          name
        }
        items {
          id
          type
          ... on CBDeliverableFileItem {
            id
            key
            name
            task {
              id
              type
              title
              description
              orderIdx
              hrType
              hrPlatform
              ... on CampaignContentTask {
                mediaType
              }
            }
            type
            url
          }
          ... on CBDeliverableCopy {
            copy
          }
        }
      }
      deliverablesV2 {
        ...CbDeliverableV2Fields
      }
      submissions {
        id
        type
        ... on CampaignIGLiveSubmission {
          imageKey
          url
        }
        ... on CampaignTTShareSubmission {
          ttShareLink: link
          ttShareImageKey: imageKey
          ttUrl: url
        }
        ... on CampaignIGShareSubmission {
          igShareLink: link
          igShareImageKey: imageKey
          igShareUrl: url
        }
        ... on CampaignTWShareSubmission {
          twShareLink: link
          twShareImageKey: imageKey
          twUrl: url
        }
        ... on CampaignIGPostSubmission {
          link
        }
        ... on CampaignIGReelSubmission {
          link
        }
        ... on CampaignIGStorySubmission {
          imageKey
          url
        }
        ... on CampaignTTVideoSubmission {
          link
        }
        ... on CampaignTTLiveSubmission {
          imageKey
          url
        }
        ... on CampaignTTStorySubmission {
          imageKey
          url
        }
        ... on CampaignTWTweetSubmission {
          link
        }
        ... on CampaignTWLiveSubmission {
          link
        }
        ... on CampaignTWVideoSubmission {
          link
        }
        ... on CampaignLiveAppearanceSubmission {
          streamCohostToken
          event {
            schedule {
              timezone
            }
          }
          stream {
            id
            streamStatus
            name
            description
            slug
            imageFileKey
            imageURL
            mainImageUrl
            scheduleDateTz
            scheduleDateUtc
            isPurchased
            hashtags {
              id
              name
            }
            mentions {
              id
              mentionType
              store {
                id
              }
              targetType
              unregisteredContact {
                id
              }
            }
            store {
              slug
            }
          }
        }
        ... on CampaignRecordedAppearanceSubmission {
          streamCohostToken
          event {
            schedule {
              timezone
            }
          }
          draftStream {
            id
            description
            slug
            imageFileKey
            imageURL
            mainImageUrl
            name
            streamStatus
            scheduleDateTz
            scheduleDateUtc
            isPurchased
            hashtags {
              id
              name
            }
            mentions {
              id
              mentionType
              store {
                id
              }
              targetType
              unregisteredContact {
                id
              }
            }
            store {
              slug
            }
          }
        }
      }
      campaign {
        budget
        id
        name
        status
        type
        createdAt
        dueDate
        applicationDeadlineEnabled
        usageRights
        usageRightsText
        search {
          sports
          smPlatforms
        }
        activities {
          id
          type
          ... on SocialActivity {
            id
            type
            brandName
          }
          ... on SocialShareActivity {
            id
            type
            brandName
          }
          ... on UgcActivity {
            id
            type
            brandName
          }
        }
        agency {
          id
          agencyDetails {
            id
            companyName
          }
        }
      }
    }
  }
  ${CbDeliverableV2Fields},
  ${DueDateFields}
`;

export const GET_ATHLETE_CB_TASK_EXTENSION_HISTORY = gql`
  query GetAthleteCbTaskExtensionHistory(
    $input: GetCbTaskExtensionHistoryInput!
  ) {
    getAthleteCbTaskExtensionHistory(input: $input) {
      id
      agencyFeedback
      createdAt
      updatedAt
      deliverable {
        id
        status
      }
      reason
      requestedExtensionDays
      newDate
      reviewedAt
      status
      type
    }
  }
`;

export const GET_VIDEO_TRANSCODING_JOBS = gql`
  query GetVideoTranscodingJobs($input: GetVideoTranscodingJobsInput!) {
    getVideoTranscodingJobs(input: $input) {
      id
      status
      progress
      inputKey
      resultKey
      errorMessage
      createdAt
      duration
      subtype
      thumbnailKey
    }
  }
`;

export const CALCULATE_OFFER_GROSS_FROM_NET = gql`
  query CalculateOfferGrossFromNet($input: CalculateGrossFromNetInput!) {
    calculateOfferGrossFromNet(input: $input) {
      grossAmount
    }
  }
`;
